<template>
  <div>
    <DashboardHeader
      v-if="showDashboardHeader"
      :title="title"
      :last-updated="lastUpdated" />

    <NationwideControlTracker
      v-if="showNationwideControlTracker"
      :office="params.nationwideControlOffice"
      :show-needed-to-win="params.nationwideControlOffice === 'Senate' && Boolean(params.s3Path)" />

    <template v-if="params.mapType !== 'Nationwide Control Tracker'">
      <button
        v-if="showDashboardMap && selectedResult"
        aria-label="Back to Nationwide Map"
        class="back-to-dashboard"
        type="button"
        @click="backToNationwideMap">
        &#8592; {{ $t("message.backToNationwideMap") }}
      </button>

      <embed-header
        v-if="selectedResult"
        :title="title"
        :results="selectedResult"
        :geo-data="geoData"
        :geo-type="geoType">
        <template #tabs>
          <tabs
            v-if="showTabs"
            :results="results"
            :selected-race="selectedResult?.race_id"
            :selected-tab="selectedTab"
            :tabs="params.showTabs ? params.tabs : []"
            :show-dashboard-map="showDashboardMap"
            @change="office = $event" />
        </template>

        <template #right>
          <mini-map
            v-if="params.showMiniMap"
            :district="parseInt(selectedResult.district, 10)"
            :district-type="districtType"
            :state="selectedResult.state_name" />
        </template>

        <template
          v-if="selectedResult.electionType && selectedResult.electionType.includes('primary')"
          #view-all>
          <div class="flex flex-wrap items-center gap-4">
            <a
              class="hill-button hill-view-all"
              target="_blank"
              :href="`https://elections2024.thehill.com/primaries/${selectedResult.state_name.toLowerCase().replace(' ', '-')}`">
              View Full Results
            </a>
          </div>
        </template>
      </embed-header>

      <div v-if="showDashboardMap && !selectedResult">
        <NationwideLegend :show-runoff="showRunoff" />
        <nationwide-map
          :key="nationwideMapKey"
          map-id="dashboard"
          :geo-type="nationwideMapGeoType"
          :office="selectedOffice"
          :results="results.filter((r) => r.office === selectedOffice)"
          :loading="loading"
          @on-click="setSelectedResultIndex(results.indexOf($event))"
          @change-key="(newKey) => (nationwideMapKey = newKey)" />
      </div>
      <div
        v-else
        class="gap-4 md:flex">
        <div class="flex-grow">
          <div
            :class="[
              'gap-4',
              params.showAd ? '' : 'md:flex',
              params.headliner ? 'flex flex-col' : '',
            ]">
            <div class="flex-grow align-top">
              <topline
                :last-updated="lastUpdated"
                :loading="loading"
                :results="selectedResult"
                :geo-data="geoData"
                :geo-type="geoType"
                :footer-data="selectedGeoResults"
                :show-precincts="reporting === 'Precincts Reporting'"
                :topline-params="params"
                class="py-0" />
              <p
                v-if="params.raceIds.includes(25407)"
                style="font-size: 11px; color: gray">
                * Statewide Delegate Equivalents
              </p>

              <div class="text-zinc-200 flex justify-between gap-2 text-sm">
                <p style="color: gray">* Incumbent</p>

                <p class="text-zinc-200 text-sm">
                  Election Date:
                  {{ formatShortDate(new Date(selectedResult.race_date.replace(/-/gu, "/"))) }}
                </p>
              </div>
            </div>
            <div
              v-if="showMap"
              :class="[
                params.showAd
                  ? 'lg:w-full'
                  : `${params.headliner ? 'max-auto w-full' : 'mt-4 md:w-1/2 lg:w-1/2'}`,
              ]">
              <single-race-map
                v-if="showSingleRaceMap"
                :key="`${selectedResult.race_id}-map`"
                :results="selectedResult"
                :geo-type="singleRaceGeoType"
                :show-cities="params.showCities" />
              <state-leg-map
                v-else-if="params.mapType === 'State Leg'"
                :results="results"
                :selected-result="selectedResult"
                :district-type="districtType"
                :district="parseInt(selectedResult.district, 10)" />
            </div>
          </div>
        </div>
        <template v-if="params.showAd">
          <div
            v-if="creator"
            style="width: 300px; height: 250px; margin: 0 auto"
            class="flex items-center justify-center rounded-md bg-gray-100 font-bold">
            Ad
          </div>
          <publir-ad v-else />
        </template>
      </div>
    </template>

    <MapTooltip
      :tooltip-always-left="tooltipAlwaysLeft"
      :tooltip-always-top="params.headliner" />
  </div>
</template>

<script>
import { mapActions } from "vuex";

import { EmbedHeader, PublirAd, Topline } from "./standard";
import Tabs from "@/components/ui/Tabs";
import { ToplineFooter } from "./standard/toplineWidgets";
import { LastUpdated } from "@/components/standard/footer";
import MiniMap from "@/components/maps/MiniMap";
import NationwideMap from "@/components/maps/NationwideMap";
import SingleRaceMap from "@/components/maps/SingleRaceMap";
import StateLegMap from "@/components/maps/StateLegMap";
import NationwideControlTracker from "@/components/maps/NationwideControlTracker";
import NationwideLegend from "@/components/maps/NationwideLegend";
import DashboardHeader from "@/components/standard/DashboardHeader";
import MapTooltip from "@/components/maps/MapTooltip";
import { formatDate, formatShortDate, formatPollCloseDate } from "../helpers/formatting";

const components = {
  EmbedHeader,
  LastUpdated,
  MiniMap,
  NationwideMap,
  PublirAd,
  SingleRaceMap,
  StateLegMap,
  Tabs,
  Topline,
  ToplineFooter,
  NationwideControlTracker,
  NationwideLegend,
  DashboardHeader,
  MapTooltip,
};

const props = {
  creator: {
    type: Boolean,
    required: false,
  },
  params: {
    type: Object,
    required: true,
  },
  geoType: {
    type: String,
    required: false,
  },
  results: {
    type: Array,
    required: true,
  },
  selectedResult: {
    required: true,
  },
  loading: {
    type: Boolean,
    required: false,
    default: false,
  },
};

const computed = {
  lastUpdated() {
    if (this.selectedResult && this.selectedResult.lastUpdated) {
      return this.selectedResult.lastUpdated;
    } else if (this.params.mapType === "Nationwide Control Tracker") {
      return new Date().toLocaleString();
    } else {
      return this.results[0].lastUpdated;
    }
  },
  pollsClose() {
    return this.results[0].poll_close_time;
  },
  geoData() {
    if (!this.selectedResult) return;
    if (this.geoType === "county" || this.geoType === "vcu") return this.selectedResult.counties;
    return [];
  },
  districtType() {
    const key = {
      "President": "state",
      "US House": "cd",
      "State House": "sldl",
      "State Assembly": "sldl",
      "State Senate": "sldu",
      "General Assembly": "sldl",
      "House of Delegates": "sldl",
    };
    if (!key[this.selectedResult.office]) return "state";
    return key[this.selectedResult.office];
  },
  geoSelection() {
    return this.$store.getters.geoSelection;
  },
  selectedGeoResults() {
    if (typeof this.geoSelection === "undefined" || this.geoSelection === null) {
      return this.selectedResult;
    }

    return this.geoData.find((d) => {
      const name = d[this.geoType] ?? d["vcu"] ?? "";

      return name.toLowerCase() === this.geoSelection.toLowerCase();
    });
  },
  selectedOffice() {
    return this.selectedResult ? this.selectedResult.office : this.office;
  },
  selectedTab() {
    const office = this.selectedOffice;
    if (this.params.tabs.includes(office)) return office;
    return "More";
  },
  title() {
    if (this.params.dashboardTitle && !this.selectedResult) {
      return this.params.dashboardTitle;
    }

    if (this.params.electionName) {
      return this.params.electionName;
    }
    let title = this.selectedResult
      ? this.selectedResult.full_title
      : null || this.params.dashboardTitle;
    const districtIsNum = parseInt(this.selectedResult.district) == this.selectedResult.district;
    if (title.includes("District ") && districtIsNum)
      title = title.replace(/District \d+/, `District ${this.selectedResult.district}`);

    return title;
  },
  showDashboardMap() {
    return this.params.mapType === "Nationwide";
  },
  showSingleRaceMap() {
    const conditionOne = this.params.mapType === "State";
    const conditionTwo = this.showDashboardMap && Boolean(this.selectedResult);
    return Boolean(this.geoType) && (conditionOne || conditionTwo);
  },
  showNationwideControlTracker() {
    if (this.params.mapType === "Nationwide Control Tracker") return true;

    return (
      this.showDashboardMap &&
      !this.selectedResult &&
      this.results &&
      this.results.length &&
      this.params.congressControlDataURL &&
      this.params.congressControlDataURL.length &&
      ["US House", "US Senate", "President"].includes(this.results[0].office)
    );
  },
  showTabs() {
    return this.selectedResult && (this.params.showTabs || this.results.length > 1);
  },
  nationwideMapGeoType() {
    if (this.results && this.results.length && this.results[0].office === "US House") return "cd";
    return "state";
  },
  showMap() {
    if (this.selectedResult && this.selectedResult.candidates.length === 1) return false;
    if (this.showDashboardMap && this.params.nationwideControlOffice === "House") return false;
    return Boolean(this.params.mapType);
  },
  showRunoff() {
    return (
      this.results &&
      this.results.length &&
      this.results[0].electionType === "general" &&
      ["US House", "US Senate"].includes(this.results[0].office)
    );
  },
  singleRaceGeoType() {
    if (this.showDashboardMap) return "county";

    return this.geoType;
  },
  showDashboardHeader() {
    return (
      this.params.mapType === "Nationwide Control Tracker" ||
      (this.showDashboardMap && !this.selectedResult)
    );
  },
  tooltipAlwaysLeft() {
    return (
      this.showMap &&
      !this.params.showAd &&
      !this.params.headliner &&
      (this.showSingleRaceMap || this.params.mapType === "State Leg")
    );
  },
  reporting() {
    const apiReportingType = this.selectedResult.reporting_type;

    if (this.params.reportingType === "Auto") {
      if (apiReportingType === "precincts") {
        return "Precincts Reporting";
      } else if (apiReportingType === "estimated") {
        return "Turnout";
      }
    }

    return this.params.reportingType;
  },
};

const methods = {
  ...mapActions(["setParams", "setSelectedResultIndex", "setGeoSelection"]),
  backToNationwideMap() {
    this.setSelectedResultIndex(null);
    this.setGeoSelection(null);
    this.office = this.results[0].office;
  },
  updateEmbedHeight() {
    if (this.previousHeight === document.body.offsetHeight) return;

    const removeScrollbars = 1;
    this.previousHeight = document.body.offsetHeight + removeScrollbars;

    const newHeightPayload = {
      id: this.params.id,
      height: document.body.offsetHeight + removeScrollbars,
    };

    window.parent.postMessage(newHeightPayload, "*");
  },
  createHeightObserver() {
    const observerTarget = document.body;
    const observerConfig = {
      attributes: true,
      childList: true,
      subtree: true,
    };
    const observer = new MutationObserver(this.updateEmbedHeight);
    observer.observe(observerTarget, observerConfig);

    this.heightObserver = observer;
  },
  formatDate,
  formatShortDate,
  formatPollCloseDate,
};

export default {
  components,
  props,
  data() {
    return {
      office: null,
      heightObserver: null,
      previousHeight: 0,
      nationwideMapKey: Math.random(),
    };
  },
  computed,
  beforeMount() {
    if (this.params.mapType !== "Nationwide Control Tracker") this.office = this.results[0].office;
    this.setParams(this.params);
  },
  mounted() {
    this.createHeightObserver();
    this.updateEmbedHeight();

    setInterval(this.updateEmbedHeight, 3000);
  },
  unmounted() {
    if (this.heightObserver) this.heightObserver.disconnect();
  },
  methods,
};
</script>

<style scoped>
.back-to-dashboard {
  background-color: var(--dem-color);
  border-radius: 1em;
  color: #fff;
  font-size: 14px;
  margin-bottom: 1em;
  padding: 0.25em 1em;
}

.back-to-dashboard:hover {
  opacity: 0.9;
}
</style>
